/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	View,
	BoxView,
	Pressable,
	Tooltip,
	Switch,
	ActionButton,
} from '@components/atoms'
import { I18nEditor } from '@components/molecules'
import { DndTree, AdminEditMenu } from '@components/systems'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'

import { useNavigate } from 'react-router-dom'
import {
	useAppSelector,
	useAppDispatch,
	adminActions,
	MenuTreeNodeType,
	EditMenuType,
	CrudType,
} from '@redux/index'
import { I18nType } from '@schemas'
import { Path } from '@utils'

/** 메뉴 관리 — 좌 트리 | 중 편집 | 우 다국어. 세 칸은 헤어라인으로만 나뉜다 */
const MenuManagementPage = () => {
	// hook
	const navigate = useNavigate()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)
	const menuTreeList = useAppSelector(state => state.admin.menuTreeList)
	const editTempMenu: EditMenuType = useAppSelector(state => state.admin.editTempMenu)
	const selectedMenu: EditMenuType = useAppSelector(state => state.admin.selectedMenu)

	// state
	const [isMenuEditMode, setIsMenuEditMode] = React.useState<boolean>(false)
	const [i18n, setI18n] = React.useState<I18nType[]>([])

	React.useEffect(() => {
		setI18n(editTempMenu.i18n)
	}, [editTempMenu.i18n])

	const onCreateMenuClick = React.useCallback(() => {
		reduxDispatch(adminActions.EDIT_MENU_MODE('create-dir'))
		navigate('/admin/create-menu')
	}, [])

	const onMenuEditModeToggle = React.useCallback(() => {
		setIsMenuEditMode(!isMenuEditMode)
	}, [isMenuEditMode])

	const onMenuTreeDrop = React.useCallback((newTree: MenuTreeNodeType[]) => {
		const tempNewTree = Path.reorderDroppedMenuTree(newTree)
		reduxDispatch(adminActions.UPDATE_MULTIPLE_MENU_LIST([...tempNewTree]))
	}, [])

	const onNodeClick = (node: MenuTreeNodeType) => {
		const nodeId: string = node.id.toString() ?? node.id
		const nodeParent: string = node.parent.toString() ?? node.parent
		const newUri: string = Path.getUriByPath(`/${node.text}`)

		const newEditMenu = {
			id: nodeId,
			parent: nodeParent,
			name: node.text,
			type: 'web',
			path: node.path ?? `root/${node.text}`,
			uri: node.uri ?? newUri,
			icon: node.icon ?? 'cog',
			order: node.order ?? 0,
			isActivate: node.isActivate ?? false,
			i18n: node.i18n ?? [],
		}
		reduxDispatch(adminActions.EDIT_TEMP_MENU(newEditMenu))
		reduxDispatch(adminActions.SELECTED_MENU(newEditMenu))
	}

	const onNodeActivateClick = (node: MenuTreeNodeType) => {
		const newNode = {
			...node,
			isActivate: !node.isActivate,
		}

		reduxDispatch(adminActions.UPDATE_MULTIPLE_MENU_LIST([{ ...newNode }]))
	}

	const onI18nEditorSave = (newI18n: I18nType) => {
		reduxDispatch(adminActions.UPDATE_MENU_I18N(newI18n))
	}

	return (
		<View css={style}>
			<BoxView
				className="TreeOuterView"
				title="메뉴"
				rightHeaderAccessory={
					<View css={{ display: 'flex', gap: 4 }}>
						{crud.includes('C') && (
							<Tooltip title="새 메뉴 추가">
								<ActionButton onClick={onCreateMenuClick} iconName="plus" />
							</Tooltip>
						)}
						<Tooltip title="편집">
							<ActionButton
								variant={isMenuEditMode ? 'outlined' : 'text'}
								onClick={onMenuEditModeToggle}
								iconName="cog"
							/>
						</Tooltip>
					</View>
				}
			>
				<DndTree
					data={menuTreeList}
					rootId="root"
					onDrop={onMenuTreeDrop}
					showDragHandle={isMenuEditMode}
					selectedMenuId={selectedMenu.id}
					onNodeClick={onNodeClick}
					nodeRightAccessory={(node: MenuTreeNodeType) => {
						return (
							<>
								{isMenuEditMode ? (
									<Pressable onClick={() => onNodeActivateClick(node)}>
										<Switch
											size="small"
											checked={node.isActivate}
											inputProps={{ 'aria-label': 'controlled' }}
										/>
									</Pressable>
								) : null}
							</>
						)
					}}
				/>
			</BoxView>

			<View className="menuEditOuter">
				<BoxView className="menuEditorView" title="메뉴 편집">
					<View className="menuEditorInnerView">
						<AdminEditMenu />
					</View>
				</BoxView>

				<BoxView className="i18nEditorView" title="다국어 관리">
					<View className="i18nEditorInnerView">
						<I18nEditor
							i18n={i18n}
							tableName="MENU"
							tableId={editTempMenu.id}
							menuId=""
							value=""
							disabled={editTempMenu.id === ''}
							onSave={onI18nEditorSave}
						/>
					</View>
				</BoxView>
			</View>
		</View>
	)
}

export default MenuManagementPage

const style = css({
	...boShared,
	display: 'flex',
	height: '100%',

	'.TreeOuterView': {
		width: '24rem',
		minWidth: '10rem',
		flexShrink: 0,
		borderRight: `1px solid ${mono.line}`,
	},

	'.menuEditOuter': {
		display: 'flex',
		flex: 1,
		minWidth: 0,
		height: '100%',

		'.menuEditorView': {
			flex: 1,
			minWidth: 0,
			maxWidth: '44rem',
			borderRight: `1px solid ${mono.line}`,

			'.menuEditorInnerView': {
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'flex-start',
				width: '100%',
			},
		},

		'.i18nEditorView': {
			width: '20rem',
			flexShrink: 0,
			height: '100%',

			'.i18nEditorInnerView': {
				width: '100%',
				height: '100%',
			},
		},
	},
})
